import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

import { FunctionColor, LightModeProvider } from '@lux/core';
import { GridCol, GridRow, Grid } from '@lux/grid';
import { Button, ButtonColor, ButtonType } from '@lux/buttons/tw';
import { Title, TitlePreset } from '@lux/title/tw';
import { Preset, Text } from '@lux/text/tw';

import { ErrorBoundary } from '@browsing/components';
import { Campaign, CategoryTab, GlobalRetailEvent } from '@shared/types/mylounge';
import { trackSelectContent, trackViewPromotion } from '@browsing/tracking';

import GRECarousel from './gre-carousel';

interface GRECarouselComponentProps {
    greData: GlobalRetailEvent | undefined;
    greCarouselCampaigns: Campaign[] | undefined;
    greTabData: CategoryTab | undefined;
    isCyberWeek?: boolean;
}

export const GRECarouselComponent: React.FC<GRECarouselComponentProps> = ({
    isCyberWeek,
    greData,
    greCarouselCampaigns,
    greTabData,
}) => {
    const navigate = useNavigate();
    const { ref: greInViewRef, inView: greInView } = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    useEffect(() => {
        if (!greInView) return;

        trackViewPromotion({ element: 'gre_carousel', creativeName: 'gre_carousel', items: [] });
    }, [greInView]);

    const handleSeeMoreGRECampaigns = () => {
        if (!greTabData) return;

        trackSelectContent('gre_carousel', 'click', { contentId: 'see_all_deals_button' });
        navigate(`/event/${greTabData.id}`);
    };

    return (
        <ErrorBoundary componentName="GRECarousel">
            <div
                ref={greInViewRef}
                className="pt-xl gap-l -mt-[12.5rem] flex w-full flex-col overflow-hidden"
            >
                <Grid>
                    <GridRow>
                        <GridCol span={12}>
                            <div id="gre-carousel-title" className="px-s isolate w-full">
                                <LightModeProvider>
                                    <Title
                                        preset={TitlePreset.M}
                                        color={FunctionColor.function0}
                                        className={`m-0 w-full text-center${isCyberWeek ? ' !text-complementary-neon-fresh' : ''}`}
                                    >
                                        {greData?.title}
                                    </Title>
                                    <Text
                                        preset={Preset.M}
                                        color={FunctionColor.function0}
                                        className={`m-0 block w-full text-center${isCyberWeek ? ' !text-complementary-neon-fresh' : ''}`}
                                    >
                                        {greData?.content}
                                    </Text>
                                </LightModeProvider>
                            </div>
                        </GridCol>
                    </GridRow>
                </Grid>
                <div className="bg-function-0 pb-m mb-s">
                    <GridRow>
                        <GridCol span={12}>
                            <GRECarousel campaigns={greCarouselCampaigns} />
                        </GridCol>
                    </GridRow>
                    <GridRow>
                        <GridCol span={12}>
                            <Button
                                buttonType={ButtonType.secondary}
                                color={ButtonColor.dark}
                                fullWidth
                                onClick={handleSeeMoreGRECampaigns}
                            >
                                {greData?.buttonText}
                            </Button>
                        </GridCol>
                    </GridRow>
                </div>
            </div>
        </ErrorBoundary>
    );
};
